import * as turf from '@turf/turf';

const geometryCache = new Map();

export const LAYER_OUTLINE_CONFIG = {
  wallWidth: 400,
  wallHeight: 3500,
  interiorHeight: 1500,
  wallColor: 'white',
  wallOpacity: 0.4,
  interiorColor: '#70e053',
  interiorOpacity: 0.5,
};

const calculateLayerScaling = (geometry: any) => {
  if (!geometry || !geometry.coordinates) {
    return { widthScale: 1, heightScale: 1, interiorHeightScale: 1, areaKm2: 0 };
  }

  try {
    const feature = turf.feature(geometry);
    const area = turf.area(feature);
    const areaKm2 = area / 1000000;
    const referenceArea = 10;

    const widthScale = Math.max(
      0.02,
      Math.min(
        5.0,
        0.15 +
          (0.85 * Math.log10((areaKm2 + 0.01) / 0.2)) /
            Math.log10(referenceArea / 0.01),
      ),
    );
    const heightScale = Math.max(
      0.001,
      Math.min(
        10.0,
        0.001 +
          0.999 *
            Math.pow(
              Math.log10((areaKm2 + 0.01) / 0.2) /
                Math.log10(referenceArea / 0.01),
              2.5,
            ),
      ),
    );
    const interiorHeightScale = Math.max(
      0.002,
      Math.min(
        3.0,
        0.002 +
          0.298 *
            Math.pow(
              Math.log10((areaKm2 + 0.01) / 0.01) /
                Math.log10(referenceArea / 0.01),
              1.8,
            ),
      ),
    );

    return { widthScale, heightScale, interiorHeightScale, areaKm2 };
  } catch (error) {
    console.error('Error calculating layer scaling:', error);
    return { widthScale: 1, heightScale: 1, interiorHeightScale: 1, areaKm2: 0 };
  }
};

export const computeLayerOutlineAsync = async (
  geometry: any,
  baseOutlineWidthMeters: number,
  cacheKey: string,
): Promise<any> => {
  if (geometryCache.has(cacheKey)) {
    return geometryCache.get(cacheKey);
  }

  return new Promise((resolve, reject) => {
    try {
      if (!geometry || !geometry.coordinates) {
        reject(new Error('Invalid geometry'));
        return;
      }

      setTimeout(() => {
        try {
          const feature = turf.feature(geometry);
          const { widthScale, heightScale, interiorHeightScale, areaKm2 } =
            calculateLayerScaling(geometry);
          const scaledOutlineWidth = baseOutlineWidthMeters * widthScale;

          const outerBuffer = turf.buffer(feature, scaledOutlineWidth / 1000, {
            units: 'kilometers',
          });

          if (!outerBuffer) {
            reject(new Error('Failed to create buffer'));
            return;
          }

          const featureCollection = turf.featureCollection([outerBuffer, feature]);
          const outlineGeometry = turf.difference(featureCollection);

          if (!outlineGeometry) {
            reject(new Error('Failed to create outline geometry'));
            return;
          }

          const result = {
            originalGeometry: geometry,
            outlineGeometry: outlineGeometry.geometry,
            outlineWidthMeters: scaledOutlineWidth,
            scaling: { widthScale, heightScale, interiorHeightScale, areaKm2 },
          };

          geometryCache.set(cacheKey, result);
          resolve(result);
        } catch (error) {
          console.error('Error in async computation:', error);
          reject(error);
        }
      }, 0);
    } catch (error) {
      console.error('Error creating layer outline geometry:', error);
      reject(error);
    }
  });
};

export const createLayerOutlineSync = (
  geometry: any,
  baseOutlineWidthMeters = 50,
): any => {
  const cacheKey = JSON.stringify({ geometry, baseOutlineWidthMeters });

  if (geometryCache.has(cacheKey)) {
    return geometryCache.get(cacheKey);
  }

  try {
    if (!geometry || !geometry.coordinates) {
      console.error('Invalid geometry for layer outline');
      return null;
    }

    const feature = turf.feature(geometry);
    const { widthScale, heightScale, interiorHeightScale, areaKm2 } =
      calculateLayerScaling(geometry);
    const scaledOutlineWidth = baseOutlineWidthMeters * widthScale;

    const outerBuffer = turf.buffer(feature, scaledOutlineWidth / 1000, {
      units: 'kilometers',
    });

    if (!outerBuffer) {
      console.error('Failed to create buffer geometry');
      return null;
    }

    const featureCollection = turf.featureCollection([outerBuffer, feature]);
    const outlineGeometry = turf.difference(featureCollection);

    if (!outlineGeometry) {
      console.error('Failed to create outline geometry');
      return null;
    }

    const result = {
      originalGeometry: geometry,
      outlineGeometry: outlineGeometry.geometry,
      outlineWidthMeters: scaledOutlineWidth,
      scaling: { widthScale, heightScale, interiorHeightScale, areaKm2 },
    };

    geometryCache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Error creating layer outline geometry:', error);
    return null;
  }
};
